import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ShopRes from "types/res/shop/ShopRes";
import styled from "styled-components";
import CommonTable, { CommonTableProps } from "components/CommonTable";
import {
  addCosplay,
  bulkInsertCosplay,
  deleteCosplay,
  fetchCosplay,
  updateCosplay,
} from "redux/actions/cosplay";
import CosplayRes from "types/res/cosplay/CosplayRes";
import { fetchShops } from "redux/actions/shop";
import { FormType } from "components/FormField";
import CreateCosplayReq from "types/req/cosplay/CreateCosplayReq";
import UpdateCosplayReq from "types/req/cosplay/UpdateCosplayReq";
import { Box, Button } from "@material-ui/core";
import CsvImport from "components/CsvImport";
import { CsvFieldType } from "utils/CsvUtils";

const Cosplay: React.FC = () => {
  const { t } = useTranslation("master");
  const dispatch = useDispatch();

  const companyId = useSelector((state) => state.account.staff.companyId);
  const shops = useSelector((state) => state.shop);
  const cosplay = useSelector((state) => state.cosplay);
  const [openCsvImport, setOpenCsvImport] = useState(false);

  useEffect(() => {
    dispatch(fetchShops(companyId));
    dispatch(fetchCosplay(companyId));
  }, [companyId, dispatch]);

  const headers: { key: keyof CosplayRes; label: string }[] = [
    { key: "name", label: t("cosplay.headers.name") },
    { key: "totalFee", label: t("cosplay.headers.totalFee") },
    { key: "shopFee", label: t("cosplay.headers.shopFee") },
    { key: "castFee", label: t("cosplay.headers.castFee") },
    { key: "sort", label: t("cosplay.headers.sort") },
    { key: "isGuestShow", label: t("cosplay.headers.isGuestShow") },
    { key: "shops", label: t("cosplay.headers.shops") },
  ];

  const forms = [
    {
      label: t("cosplay.forms.name"),
      key: "name",
      type: FormType.Text,
    },
    {
      label: t("cosplay.forms.totalFee"),
      key: "totalFee",
      type: FormType.Number,
    },
    {
      label: t("cosplay.forms.shopFee"),
      key: "shopFee",
      type: FormType.Number,
    },
    {
      label: t("cosplay.forms.castFee"),
      key: "castFee",
      type: FormType.Number,
    },
    {
      label: t("cosplay.forms.sort"),
      key: "sort",
      type: FormType.Number,
    },
    {
      label: t("cosplay.forms.isGuestShow"),
      key: "isGuestShow",
      type: FormType.Boolean,
      options: [
        { id: "0", name: t("cosplay.options.hidden") },
        { id: "1", name: t("cosplay.options.shown") },
      ],
    },
    {
      label: t("cosplay.forms.shops"),
      key: "shops",
      type: FormType.MultiOptions,
      options: shops.map((s) => ({ id: Number(s.shopId), name: s.name })),
      value: (val: any) => val.map((s: any) => s.shopId),
    },
  ];

  const csvHeader = [
    {
      label: "名前",
      key: "name",
      type: CsvFieldType.Text,
    },
    {
      label: "料金※数字のみ",
      key: "totalFee",
      type: CsvFieldType.Number,
    },
    {
      label: "店舗料金※数字のみ",
      key: "shopFee",
      type: CsvFieldType.Number,
    },
    {
      label: "キャスト料金※数字のみ",
      key: "castFee",
      type: CsvFieldType.Number,
    },
    {
      label: "並び順",
      key: "sort",
      type: CsvFieldType.Number,
    },
    {
      label: "顧客モバイル表示",
      key: "isGuestShow",
      type: CsvFieldType.Boolean,
    },
    {
      label: "店舗",
      key: "shops",
      type: CsvFieldType.Array,
      relation: shops,
      relationKey: "name",
      relationId: "shopId",
    },
  ];

  return (
    <>
      <Box
        display="flex"
        justifyContent="flex-end"
        marginTop={1}
        marginRight={2}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenCsvImport((prev) => !prev)}
          style={{ marginLeft: "30px" }}
        >
          {t("cosplay.csvImport")}
        </Button>
      </Box>
      {openCsvImport && (
        <CsvImport
          templatePath={"/csv/cosplay.csv"}
          templateName={"cosplay_template.csv"}
          csvHeader={csvHeader}
          addType={CreateCosplayReq}
          addFunc={(formData) => bulkInsertCosplay(companyId, formData)}
        />
      )}
      <CosplayTable
        title={t("cosplay.title")}
        formId="cosplayForm"
        forms={forms}
        rows={headers}
        data={cosplay}
        addFunc={(formData) => addCosplay(companyId, formData)}
        updateFunc={(formData) => updateCosplay(companyId, formData)}
        deleteFunc={(item: any) =>
          deleteCosplay(companyId, {
            cosplayId: item.cosplayId,
          })
        }
        addType={CreateCosplayReq}
        updateType={UpdateCosplayReq}
        values={[
          (s) => s.name,
          (s) => s.totalFee,
          (s) => s.shopFee,
          (s) => s.castFee,
          (s) => s.sort || t("cosplay.values.unset"),
          (s) => (s.isGuestShow ? t("cosplay.values.shown") : t("cosplay.values.hidden")),
          (s) =>
            s.shops
              .map(
                (shopRes: ShopRes) =>
                  shops.find((shop) => shop.shopId === shopRes.shopId)?.name
              )
              .join(",") || t("cosplay.values.notAssigned"),
        ]}
      />
    </>
  );
};

const CosplayTable = styled<
  React.FC<CommonTableProps<CosplayRes, CreateCosplayReq, UpdateCosplayReq>>
>(CommonTable)`
  margin-top: 24px;
`;

export default Cosplay;
